import Button from 'components/Basic/Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { 
	resetDeletedObstructionIDs, 
	resetEditedObstructions, 
	resetManualObs, 
	setEnableObsMode, 
	showAddObstructionsPopUp, 
	showDeleteObstructionsPopUp, 
	toggleAddObstructionsMode, 
	toggleDeleteObstructionsMode,
} from 'store/slices/ToolSlice';
import { useEffect } from 'react';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import useScreenDims from 'hooks/useScreenDims';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { clearToolModeHandlers, registerToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { resetUndoStack } from 'store/slices/ObstructionUndoSlice';

interface EditObstructionModeToggleProps {
  canRemoveShadingEnabled: boolean;
}

export default function EditObstructionModeToggle({canRemoveShadingEnabled}: EditObstructionModeToggleProps) {

	const { enableEditObs, deletedObstructionIDs, deleteObstructionsPopUp, manualObs,
		addObstructionsPopUp, addObstructionsMode, deleteObstructionsMode } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { activePanelMode } = useSelector((state: RootState) => state.panelSlice.data);
	const dispatch = useDispatch<AppDispatch>();
	const userHasDeletedObstructions = !!deletedObstructionIDs?.length;
	const userHasAddedObstructions = !!(Object.keys(manualObs).length);
	const queriesRunningInBackGround = useQueriesRunningInBackGround();
	const message = 'Are you sure you want to discard the edited obstructions?';

	const isAnyActionEnabled = useSelector(selectIsAnyToolActionActive);
	const disableObstructionMode = (!enableEditObs && isAnyActionEnabled) || !!queriesRunningInBackGround || activePanelMode;

	const screenDims = useScreenDims();
	const { width: screenWidth } = screenDims;
	const isMobileView = screenWidth < 860;

	function handleClick() {
		if(disableObstructionMode) {
			return;
		}
		if (userHasDeletedObstructions && enableEditObs) {
			dispatch(showDeleteObstructionsPopUp({ enable: true }));
			return;
		}
		if(userHasAddedObstructions && enableEditObs){
			dispatch(showAddObstructionsPopUp({ enable: true }));
			return;
		}
		dispatch(setEnableObsMode({ enable: !enableEditObs }));
	}

	function handleConfirmation() {
		// ------------- confirm cancel for add obs ----------------------//
		dispatch(resetManualObs());
		dispatch(toggleAddObstructionsMode({ enable: false }));
		dispatch(showAddObstructionsPopUp({ enable: false }));

		// ------------- confirm cancel for delete obs -------------------//
		dispatch(resetDeletedObstructionIDs());
		dispatch(toggleDeleteObstructionsMode({ enable: false }));
		dispatch(showDeleteObstructionsPopUp({ enable: false }));

		// ------------- reset edit obs mode -------------------//
		dispatch(resetEditedObstructions());

		dispatch(resetUndoStack());
	}

	useEffect(() => {
		registerToolModeHandlers(TOOL_ACTION.EDIT_OBSTRUCTION, {
			cancel: () => {
				dispatch(setEnableObsMode({enable:false}));
				// --------- reset delete obs mode --------------//
				dispatch(resetDeletedObstructionIDs());
				dispatch(toggleDeleteObstructionsMode({ enable: false }));
				// --------- reset add obs mode --------------//
				dispatch(resetManualObs());
				dispatch(toggleAddObstructionsMode({ enable: false }));
				dispatch(resetUndoStack());
				// dispatch(showAddObstructionsPopUp({ enable: false }));		
				// ------------- reset edit obs mode -------------------//
				dispatch(resetEditedObstructions());
			},
			cancelConfirmMessage: message,
		});
		return () => {
			clearToolModeHandlers(TOOL_ACTION.EDIT_OBSTRUCTION);
		};
	}, [addObstructionsMode, deleteObstructionsMode, dispatch, message]);

	return (
		<>
			<Button style={{
				background: `${enableEditObs ? 'var(--toolActiveBgColor)' : 'var(--defaultBgColor)'}`,
				color: `${enableEditObs ? 'var(--primary)' : 'var(--labelValueColor)'}`,
			}}
			id="edit-obstruction-mode"
			tooltipContent={'Edit Obstructions'}
			className = {`${enableEditObs ? 'active' : ''} ${canRemoveShadingEnabled ? 'btn-tools' : 'btn-tools active-shading'}`}
			onPointerDown={handleClick}
			disabled={queriesRunningInBackGround || disableObstructionMode}
			>
				<svg 
					width={isMobileView ? '20' : '21'}
					height={isMobileView ? '20' : '21'}
					style={{ opacity: disableObstructionMode ? '.65' : '1' }}
					xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
					<path d="M15.6855 10.5784L10.1855 15.6855L9.47094 18.1867C9.34408 18.6307 9.74537 19.0456 10.1934 18.9336L13.1855 18.1855L18.1855 
					13.5702M15.6855 10.5784L16.4124 9.90351C16.8325 9.51337 17.494 9.55566 17.861 9.99612L19.077 11.4553C19.418 11.8645 19.3784 
					12.469 18.9871 12.8303L18.1855 13.5702M15.6855 10.5784L18.1855 13.5702" stroke="var(--defaultIconColor)" strokeWidth="1.37"/>
					<path d="M6.18555 0.685547H2.68555C1.58098 0.685547 0.685547 1.58098 0.685547 2.68555V5.68555M6.18555 0.685547V15.6855M6.18555 
					0.685547H11.6855M6.18555 15.6855H2.68555C1.58098 15.6855 0.685547 14.7901 0.685547 13.6855V10.6855M6.18555 
					15.6855H9.68555M11.6855 0.685547H15.1855C16.2901 0.685547 17.1855 1.58098 17.1855 2.68555V5.68555M11.6855 
					0.685547V9.68555C11.6855 10.2378 11.2378 10.6855 10.6855 10.6855H0.685547M0.685547 10.6855V5.68555M17.1855 
					5.68555H0.685547M17.1855 5.68555V9.18555" stroke="var(--defaultIconColor)" strokeWidth="1.37" strokeLinecap="round"/>
				</svg>
			</Button>
			<ConfirmationModal
				open={deleteObstructionsPopUp || addObstructionsPopUp}
				onCancel={() => { deleteObstructionsPopUp ? dispatch(showDeleteObstructionsPopUp({enable:false})) :
					dispatch(showAddObstructionsPopUp({enable:false}));
				}}
				onConfirm={handleConfirmation}
				message={message} />
		</>
	);
}